import { useState } from 'react';
import { useElementWidth } from '@/hooks/useElementWidth';
import { labelEvery, niceScale } from '@/lib/chartScale';

export interface ChartSeries {
  key: string;
  label: string;
  /** A CSS colour: one of the --chart-n tokens. */
  color: string;
}

export interface ChartStep {
  key: string;
  /** Under the column. */
  label: string;
  /** In the tooltip. */
  title: string;
  /** One value for each series, in the series' order. */
  values: number[];
}

interface ColumnChartProps {
  series: ChartSeries[];
  steps: ChartStep[];
  formatValue: (value: number) => string;
  /** What the chart shows, for who cannot see it. */
  ariaLabel: string;
  /** True when the values count whole things: no gridline at half a person. */
  wholeNumbers?: boolean;
  height?: number;
}

const MARGIN = { top: 12, right: 8, bottom: 26, left: 44 };
const MAX_COLUMN = 24;
const GAP = 2; // Surface showing between two segments of a column.
const RADIUS = 4;
const FALLBACK_WIDTH = 720;
const TOOLTIP_WIDTH = 240;
const TOOLTIP_GAP = 8;

/** A column segment, rounded at the end that is data and square at the base. */
function segmentPath(x: number, y: number, width: number, height: number, rounded: boolean): string {
  const r = rounded ? Math.min(RADIUS, width / 2, height) : 0;
  return [
    `M${x},${y + height}`,
    `V${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `H${x + width - r}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `V${y + height}`,
    'Z',
  ].join(' ');
}

/**
 * Where the tooltip of a band goes: beside it, so that it never covers the
 * column it speaks of. After the band while there is room, before it if not.
 */
function tooltipPosition(bandLeft: number, band: number, width: number): number {
  const after = bandLeft + band + TOOLTIP_GAP;
  if (after + TOOLTIP_WIDTH <= width) return after;
  return Math.max(0, bandLeft - TOOLTIP_GAP - TOOLTIP_WIDTH);
}

/**
 * Columns over time, stacked when there is more than one series. The legend
 * names the series, a tooltip gives the figures of the column under the
 * pointer, and the table beside the chart gives all of them.
 */
export default function ColumnChart({
  series,
  steps,
  formatValue,
  ariaLabel,
  wholeNumbers = false,
  height = 240,
}: ColumnChartProps) {
  const [ref, width] = useElementWidth<HTMLDivElement>(FALLBACK_WIDTH);
  const [active, setActive] = useState<number | null>(null);

  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const band = plotWidth / Math.max(1, steps.length);
  const column = Math.max(2, Math.min(MAX_COLUMN, band * 0.7));

  const totals = steps.map((s) => s.values.reduce((sum, v) => sum + v, 0));
  const scale = niceScale(Math.max(0, ...totals), 4, wholeNumbers);
  const y = (value: number) => MARGIN.top + plotHeight - (value / scale.max) * plotHeight;
  const every = labelEvery(steps.length, plotWidth);

  const activeStep = active !== null ? steps[active] : undefined;
  const tooltipLeft = active === null ? 0 : tooltipPosition(MARGIN.left + band * active, band, width);

  return (
    <div>
      {series.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-muted-foreground" aria-label="Legenda">
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      <div ref={ref} className="relative" onPointerLeave={() => setActive(null)}>
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block">
          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="hsl(var(--border))"
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 8}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
                className="fill-muted-foreground text-[11px] tabular-nums"
              >
                {formatValue(tick)}
              </text>
            </g>
          ))}

          {steps.map((step, i) => {
            const x = MARGIN.left + band * i + (band - column) / 2;
            const last = step.values.reduce((top, v, s) => (v > 0 ? s : top), -1);
            let base = 0;
            return (
              <g key={step.key}>
                {active === i && (
                  <rect
                    x={MARGIN.left + band * i}
                    y={MARGIN.top}
                    width={band}
                    height={plotHeight}
                    fill="hsl(var(--muted))"
                    opacity={0.6}
                  />
                )}
                {step.values.map((value, s) => {
                  if (value <= 0) return null;
                  const top = y(base + value);
                  const bottom = y(base);
                  base += value;
                  // The gap is taken from the segment above, never from the
                  // baseline: the first segment stands on the axis.
                  const lift = s > 0 && bottom - top > GAP + 1 ? GAP : 0;
                  return (
                    <path
                      key={series[s].key}
                      d={segmentPath(x, top, column, Math.max(1, bottom - top - lift), s === last)}
                      fill={series[s].color}
                    />
                  );
                })}
                {i % every === 0 && (
                  <text
                    x={MARGIN.left + band * (i + 0.5)}
                    y={height - 8}
                    textAnchor="middle"
                    className="fill-muted-foreground text-[11px]"
                  >
                    {step.label}
                  </text>
                )}
                {/* The whole band answers to the pointer, not the column alone. */}
                <rect
                  x={MARGIN.left + band * i}
                  y={MARGIN.top}
                  width={band}
                  height={plotHeight + MARGIN.bottom}
                  fill="transparent"
                  onPointerEnter={() => setActive(i)}
                  onPointerDown={() => setActive(i)}
                />
              </g>
            );
          })}

          <line
            x1={MARGIN.left}
            x2={width - MARGIN.right}
            y1={y(0)}
            y2={y(0)}
            stroke="hsl(var(--muted-foreground))"
            strokeOpacity={0.5}
            strokeWidth={1}
          />
        </svg>

        {activeStep && active !== null && (
          <div
            role="status"
            className="absolute top-0 z-10 pointer-events-none rounded-md border border-border bg-card shadow-md px-3 py-2 text-xs"
            style={{ left: tooltipLeft, width: TOOLTIP_WIDTH }}
          >
            <p className="font-medium text-foreground mb-1 first-letter:uppercase">{activeStep.title}</p>
            {series.map((s, index) => (
              <p key={s.key} className="flex items-center justify-between gap-3 text-muted-foreground">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} aria-hidden="true" />
                  <span className="truncate">{s.label}</span>
                </span>
                <span className="tabular-nums font-medium text-foreground">
                  {formatValue(activeStep.values[index] ?? 0)}
                </span>
              </p>
            ))}
            {series.length > 1 && (
              <p className="flex items-center justify-between gap-3 mt-1 pt-1 border-t border-border text-muted-foreground">
                <span>Total</span>
                <span className="tabular-nums font-medium text-foreground">{formatValue(totals[active])}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
